import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Grid,
  Typography,
  Box,
  Paper,
  Skeleton,
  Card,
  CardContent,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Fade,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import { useParams, useNavigate } from "react-router-dom";
import IssuesSelectionList from "../IssueSelectionList";
import "../../styles/projects.css";

export const ChartDataContext = createContext();

export const ChartDataProvider = ({ projectId, children }) => {
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [reportData, setReportData] = useState(null); // Add state for report data

  const fetchChartData = useCallback(async () => {
    setChartLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/latest-report?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      const severityCounts =
        data.staticAnalysisDetails?.sonarqube?.severityCount;
      setChartData(severityCounts || null);
      setReportData(data); // Store the full report data
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setChartData(null);
      setReportData(null);
    } finally {
      setChartLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return (
    <ChartDataContext.Provider
      value={{ chartData, setChartData, chartLoading, reportData }}
    >
      {children}
    </ChartDataContext.Provider>
  );
};

const StaticAnalysis = () => {
  const { projectId } = useParams();
  const { chartData, chartLoading, reportData } = useContext(ChartDataContext);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const issueSelectionRef = useRef(null);
  const navigate = useNavigate();

  // State for the Actions dropdown menu
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Example handlers for actions – replace with actual functionality
  const handleResolvedIssues = () => {
    console.log("Show Resolved Issues");
    handleCloseMenu();
  };

  const handleCompareUpdates = () => {
    navigate("/codediff");
    handleCloseMenu();
  };

  const handlePreviousIssues = () => {
    console.log("Show Previous Issues");
    handleCloseMenu();
  };

  // Function to handle report download
  const handleDownloadReport = async () => {
    if (!reportData) return;

    // Find the static analysis data with file paths
    const staticAnalysis = reportData.staticAnalysisDetails?.sonarqube;
    if (!staticAnalysis) return;

    const token = localStorage.getItem("access_token");
    const filePath = staticAnalysis.pdf_file_path_s3 || staticAnalysis.csv_file_path_s3;
    const fileType = staticAnalysis.pdf_file_path_s3 ? "pdf" : "csv";
    const runId = reportData.run_id;

    if (!filePath || !runId) {
      console.error("No file path or run ID available");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/generate-presigned-url?run_id=${runId}&file_path_s3=${filePath}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
        }
      );
      const data = await response.json();

      if (data.presigned_url) {
        const link = document.createElement("a");
        link.href = data.presigned_url;
        link.setAttribute("download", `Static_Analysis_Report.${fileType}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Failed to get presigned URL");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // Check if report file is available
  const hasReportFile = reportData?.staticAnalysisDetails?.sonarqube?.pdf_file_path_s3 || 
                        reportData?.staticAnalysisDetails?.sonarqube?.csv_file_path_s3;

  // Compute if there are any issues based on severity counts
  const hasIssues = chartData
    ? parseInt(chartData.info || "0", 10) +
        parseInt(chartData.minor || "0", 10) +
        parseInt(chartData.major || "0", 10) +
        parseInt(chartData.critical || "0", 10) +
        parseInt(chartData.blocker || "0", 10) >
      0
    : false;

  const IssuesBarChart = () => {
    if (chartLoading) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Loading chart data...
          </Typography>
        </Box>
      );
    }

    if (!chartData) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" color="textSecondary">
            No chart data available.
          </Typography>
        </Box>
      );
    }

    const data = {
      labels: ["info", "minor", "major", "critical", "blocker"],
      datasets: [
        {
          label: "Severity Levels",
          data: [
            parseInt(chartData.info || 0, 10),
            parseInt(chartData.minor || 0, 10),
            parseInt(chartData.major || 0, 10),
            parseInt(chartData.critical || 0, 10),
            parseInt(chartData.blocker || 0, 10),
          ],
          backgroundColor: [
            "#17A2B8",
            "#6C757D",
            "#FFC107",
            "#DC3545",
            "#343A40",
          ],
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        tooltip: { enabled: true },
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const clickedIndex = elements[0].index;
          const severityLevel = data.labels[clickedIndex];
          setSelectedSeverity(severityLevel);
          if (issueSelectionRef.current) {
            issueSelectionRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }
      },
    };

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: "250px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Bar data={data} options={options} />
      </Box>
    );
  };

  const IssuesDetails = () => {
    const severityLevels = [
      {
        label: "INFO",
        count: chartData?.info || 0,
        gradient: "linear-gradient(135deg, #17A2B8, #48C6EF)",
      },
      {
        label: "MINOR",
        count: chartData?.minor || 0,
        gradient: "linear-gradient(135deg, #6C757D, #ADB5BD)",
      },
      {
        label: "MAJOR",
        count: chartData?.major || 0,
        gradient: "linear-gradient(135deg, #FFC107, #FFD700)",
      },
      {
        label: "CRITICAL",
        count: chartData?.critical || 0,
        gradient: "linear-gradient(135deg, #DC3545, #FF6B6B)",
      },
      {
        label: "BLOCKER",
        count: chartData?.blocker || 0,
        gradient: "linear-gradient(135deg, #343A40, #5A5A5A)",
      },
    ];

    return (
      <Grid container spacing={1} sx={{ height: "100%" }}>
        {severityLevels.map(({ label, count, gradient }) => (
          <Grid item xs={6} sm={4} md={4} key={label}>
            <Card
              sx={{
                background: gradient,
                color: "#fff",
                textAlign: "center",
                boxShadow: 3,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "120px",
                borderRadius: 2,
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
              onClick={() => {
                setSelectedSeverity(label.toLowerCase());
                if (issueSelectionRef.current) {
                  issueSelectionRef.current.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              <CardContent sx={{ p: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      color: "#fff",
                      width: 30,
                      height: 30,
                      mr: 1,
                      ml: 0,
                    }}
                  >
                    {label.charAt(0)}
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "15px", fontWeight: "bold" }}
                  >
                    {label}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontSize: "25px" }}>
                  {count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box className="static-analysis-container">
      {/* Render the status and action buttons only if chartData is available */}
      {false && (
        <Grid container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          {/* Status Box: full width on mobile, 8 columns on desktop */}
          <Grid item xs={12} md={6} lg={8}>
            <Box
              sx={{
                p: 1,
                backgroundColor: hasIssues ? "#ffebee" : "#e8f5e9",
                display: "flex",
                alignItems: "center",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: hasIssues ? "#d32f2f" : "#2e7d32",
                  fontWeight: "bold",
                }}
              >
                {hasIssues
                  ? "⚠️ Issues detected in last run."
                  : "✅ No Issues detected in last run."}
              </Typography>
            </Box>
          </Grid>

          {/* Download Report Button: full width on mobile, 2 columns on desktop */}
          <Grid item xs={6} md={3} lg={2}>
            <Button variant="contained" color="primary" fullWidth>
              Download Report
            </Button>
          </Grid>

          {/* Actions Dropdown: full width on mobile, 2 columns on desktop */}
          <Grid item xs={6} md={3} lg={2}>
            <Button variant="outlined" fullWidth onClick={handleOpenMenu}>
              Actions
            </Button>
            <Menu anchorEl={anchorEl} open={openMenu} onClose={handleCloseMenu}>
              <MenuItem onClick={handleResolvedIssues}>
                Show Resolved Issues
              </MenuItem>
              <MenuItem onClick={handleCompareUpdates}>
                Compare Updates
              </MenuItem>
              <MenuItem onClick={handlePreviousIssues}>
                Show Previous Issues
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
      )}
      <Paper className="static-analysis-paper" sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6" sx={{ mb: { xs: 1, sm: 0 } }}>
            Issues by Severity
          </Typography>
          
          <Button
            variant="contained"
            className="athrv-dash-primary-button"
            sx={{ mb: { xs: 1, sm: 1 } }}
            onClick={() => navigate(`/projects`)}
          >
            Back to Project List
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ height: "320px" }}>
          {/* Bar Chart */}
          <Grid item xs={12} sm={12} md={6} sx={{ height: "100%" }}>
            <IssuesBarChart />
          </Grid>

          {/* Issue Details */}
          <Grid item xs={12} sm={12} md={6} sx={{ height: "100%" }}>
            <IssuesDetails />
          </Grid>
        </Grid>
      </Paper>

      <div ref={issueSelectionRef}>
        <IssuesSelectionList
          projectId={projectId}
          selectedSeverity={selectedSeverity}
          hasReportFile={hasReportFile}
          handleDownloadReport={handleDownloadReport}
          reportData={reportData}
          chartLoading={chartLoading}
        />
      </div>
    </Box>
  );
};

export default StaticAnalysis;